import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { InputField } from "@/components/InputField/InputField";
import { classNames } from "@/utils/classNames";

import { DraftTiming } from "../utils/inboxDraft.types";
import { INVITE_NOTE_PREMIUM_LIMIT } from "../utils/inviteNote.constants";

import { TimingDropdown } from "./TimingDropdown";

/**
 * The fields of the edit-scheduled-message modal (PREC-4220): the subject on a mail, the body,
 * and the timing — an absolute day for a message with a send time, a delay after acceptance for a
 * follow-up parked behind a LinkedIn invitation. Presentational: the hook owns the state and the
 * request, so the form can be pinned without a store.
 *
 * The body is the same plain textarea the composer uses for both lanes (`DraftEditorPanel`); an
 * invitation note shows the counter against the premium cap, the coarse limit compose applies.
 */
export const EditScheduledMessageForm: FunctionComponent<{
	isMail: boolean;
	isInviteNote: boolean;
	isParked: boolean;
	message: string;
	subject: string;
	timing: DraftTiming;
	onMessageChange: (message: string) => void;
	onSubjectChange: (subject: string) => void;
	onTimingChange: (timing: DraftTiming) => void;
	portalContainer?: HTMLElement | null;
}> = ({
	isMail,
	isInviteNote,
	isParked,
	message,
	subject,
	timing,
	onMessageChange,
	onSubjectChange,
	onTimingChange,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const noteTooLong = isInviteNote && message.length > INVITE_NOTE_PREMIUM_LIMIT;
	const emptyMessage = !isInviteNote && message.trim().length === 0;

	return (
		<div className="flex flex-col gap-3">
			{isMail && (
				<label className="flex flex-col gap-1 text-xs text-text-meta">
					{t("outbox.editSchedule.subjectLabel")}
					<input
						className="input-styles w-full rounded-[10px] border px-3 py-2 text-sm text-text bg-white outline-none"
						data-testid="edit-schedule-subject"
						placeholder={t("outbox.editSchedule.subjectPlaceholder")}
						type="text"
						value={subject}
						onChange={(e) => onSubjectChange(e.target.value)}
					/>
				</label>
			)}

			<div className="flex flex-col gap-1 text-xs text-text-meta">
				{t("outbox.editSchedule.messageLabel")}
				<InputField
					className="w-full !text-sm"
					handleChange={(e) => onMessageChange(e.target.value)}
					maxAutoGrowHeightPx={240}
					multiline={true}
					name="message"
					value={message}
				/>
				{isInviteNote && (
					<span
						className={classNames("text-xs", noteTooLong ? "text-status-error" : "text-text-meta")}
						data-testid="edit-schedule-note-counter"
					>
						{noteTooLong
							? t("outbox.editSchedule.noteTooLong", { limit: INVITE_NOTE_PREMIUM_LIMIT })
							: t("outbox.editSchedule.noteCounter", {
									count: message.length,
									limit: INVITE_NOTE_PREMIUM_LIMIT,
								})}
					</span>
				)}
				{emptyMessage && (
					<span className="text-xs text-status-error" data-testid="edit-schedule-empty">
						{t("outbox.editSchedule.emptyMessage")}
					</span>
				)}
			</div>

			<div className="flex items-center gap-2 text-xs text-text-meta">
				<span>
					{isParked ? t("outbox.editSchedule.delayLabel") : t("outbox.editSchedule.sendTimeLabel")}
				</span>
				<TimingDropdown
					portalContainer={portalContainer}
					relativeToInviteDays={isParked ? 0 : undefined}
					value={timing}
					onSelect={onTimingChange}
				/>
			</div>
		</div>
	);
};
